/**
 * W-Header - Custom Header JavaScript
 * Handles: scroll effects, hover states, mega menu, mobile menu
 */

class WHeader {
  constructor() {
    this.header = document.querySelector('.header');
    if (!this.header) return;

    this.navbar = this.header.querySelector('.header__navbar');
    this.navbarMobile = this.header.querySelector('.header__navbar-mobile');
    this.burger = this.header.querySelector('.header__burger');
    this.mobileMenu = document.querySelector('.header__mobile-menu');
    this.overlay = document.querySelector('.header__overlay');
    this.megaMenuItems = this.header.querySelectorAll('[data-megamenu-trigger]');
    this.megaMenus = this.header.querySelectorAll('.header__megamenu');

    // Mobile menu panels
    this.mobileMainPanel = document.querySelector('[data-mobile-panel="main"]');
    this.mobileSubmenuPanels = document.querySelectorAll('[data-mobile-panel^="submenu-"]');
    this.mobileCloseButtons = document.querySelectorAll('[data-close-mobile-menu]');
    this.mobileOpenSubmenuButtons = document.querySelectorAll('[data-open-submenu]');
    this.mobileBackButtons = document.querySelectorAll('[data-back-to-main]');

    // Check if any section on the page requests transparent header
    this.allowTransparent = document.querySelector('[data-header-transparent]') !== null;

    this.isScrolled = false;
    this.isHovered = false;
    this.isMobileMenuOpen = false;
    this.activeMegaMenu = null;
    this.activeSubmenuPanel = null;

    this.init();
  }

  init() {
    this.setHeaderHeight();
    this.bindEvents();
    // Set initial state based on scroll position and allowTransparent
    this.checkScroll();
  }

  setHeaderHeight() {
    const headerHeight = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  bindEvents() {
    // Scroll event
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

    // Resize event - update header height
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });

    // Header hover events
    if (this.navbar) {
      this.header.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      this.header.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }

    // Burger menu
    if (this.burger) {
      this.burger.addEventListener('click', this.toggleMobileMenu.bind(this));
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeAll.bind(this));
    }

    // Mega menu triggers
    this.megaMenuItems.forEach(item => {
      item.addEventListener('mouseenter', this.handleMegaMenuEnter.bind(this));
    });

    // Mega menu containers - keep open when hovering
    this.megaMenus.forEach(menu => {
      menu.addEventListener('mouseenter', () => {
        this.keepMegaMenuOpen(menu);
      });
      menu.addEventListener('mouseleave', () => {
        this.closeMegaMenu();
      });
    });

    // Close mega menu when leaving nav area
    if (this.navbar) {
      this.navbar.addEventListener('mouseleave', this.handleNavbarLeave.bind(this));
    }

    // Mobile menu close buttons
    this.mobileCloseButtons.forEach(button => {
      button.addEventListener('click', this.closeMobileMenu.bind(this));
    });

    // Mobile submenu open buttons
    this.mobileOpenSubmenuButtons.forEach(button => {
      button.addEventListener('click', this.openMobileSubmenu.bind(this));
    });

    // Mobile back buttons
    this.mobileBackButtons.forEach(button => {
      button.addEventListener('click', this.backToMainMenu.bind(this));
    });

    // Sidecart triggers
    const sidecartTriggers = document.querySelectorAll('[data-open-sidecart]');
    sidecartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof window.openSidecart === 'function') {
          window.openSidecart();
        }
      });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAll();
      }
    });
  }

  handleScroll() {
    this.checkScroll();
  }

  handleResize() {
    this.setHeaderHeight();
  }

  checkScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.isScrolled = scrollTop > 10;
    this.updateHeaderState();
  }

  handleMouseEnter() {
    this.isHovered = true;
    this.updateHeaderState();
  }

  handleMouseLeave() {
    this.isHovered = false;
    // Small delay to allow moving to mega menu
    setTimeout(() => {
      if (!this.activeMegaMenu) {
        this.updateHeaderState();
      }
    }, 100);
  }

  updateHeaderState() {
    if (!this.header) return;

    const shouldBeSolid = this.isScrolled || this.isHovered || this.activeMegaMenu || this.isMobileMenuOpen;

    // Only allow transparent if a section on the page requests it
    if (shouldBeSolid || !this.allowTransparent) {
      this.header.classList.remove('header--transparent');
      this.header.classList.add('header--solid');
    } else {
      this.header.classList.add('header--transparent');
      this.header.classList.remove('header--solid');
    }
  }

  // Mega Menu
  handleMegaMenuEnter(e) {
    const trigger = e.currentTarget;
    const menuId = trigger.getAttribute('data-megamenu-trigger');
    const menu = this.header.querySelector(`[data-megamenu="${menuId}"]`);

    if (menu) {
      this.openMegaMenu(menu);
    }
  }

  handleNavbarLeave(e) {
    // Check if we're moving to a mega menu
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest('.header__megamenu')) {
      return;
    }
    this.closeMegaMenu();
  }

  openMegaMenu(menu) {
    // Close any open mega menu first
    this.megaMenus.forEach(m => {
      m.classList.remove('header__megamenu--active');
    });

    menu.classList.add('header__megamenu--active');
    this.activeMegaMenu = menu;
    this.isHovered = true;
    this.overlay?.classList.add('header__overlay--active');
    document.body.style.overflow = 'hidden';
    this.updateHeaderState();
  }

  keepMegaMenuOpen(menu) {
    this.activeMegaMenu = menu;
    this.isHovered = true;
  }

  closeMegaMenu() {
    this.megaMenus.forEach(menu => {
      menu.classList.remove('header__megamenu--active');
    });
    this.activeMegaMenu = null;
    this.overlay?.classList.remove('header__overlay--active');
    if (!this.isMobileMenuOpen) {
      document.body.style.overflow = '';
    }
    this.updateHeaderState();
  }

  // Mobile Menu
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    if (this.isMobileMenuOpen) {
      this.openMobileMenu();
    } else {
      this.closeMobileMenu();
    }
  }

  openMobileMenu() {
    this.isMobileMenuOpen = true;
    this.burger?.classList.add('header__burger--open');
    this.mobileMenu?.classList.add('header__mobile-menu--open');
    this.mobileMainPanel?.classList.add('header__mobile-panel--active');
    this.overlay?.classList.add('header__overlay--active');
    document.body.style.overflow = 'hidden';
    this.updateHeaderState();
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.burger?.classList.remove('header__burger--open');
    this.mobileMenu?.classList.remove('header__mobile-menu--open');
    this.mobileMainPanel?.classList.remove('header__mobile-panel--active');

    // Close any active submenu
    if (this.activeSubmenuPanel) {
      this.activeSubmenuPanel.classList.remove('header__mobile-panel--active');
      this.activeSubmenuPanel = null;
    }

    this.overlay?.classList.remove('header__overlay--active');
    document.body.style.overflow = '';
    this.updateHeaderState();
  }

  openMobileSubmenu(e) {
    const button = e.currentTarget;
    const submenuId = button.getAttribute('data-open-submenu');
    const submenuPanel = document.querySelector(`[data-mobile-panel="submenu-${submenuId}"]`);

    if (submenuPanel) {
      // Hide main panel
      this.mobileMainPanel?.classList.remove('header__mobile-panel--active');

      // Show submenu panel
      submenuPanel.classList.add('header__mobile-panel--active');
      this.activeSubmenuPanel = submenuPanel;
    }
  }

  backToMainMenu() {
    // Hide current submenu
    if (this.activeSubmenuPanel) {
      this.activeSubmenuPanel.classList.remove('header__mobile-panel--active');
      this.activeSubmenuPanel = null;
    }

    // Show main panel
    this.mobileMainPanel?.classList.add('header__mobile-panel--active');
  }

  // Close all
  closeAll() {
    this.closeMobileMenu();
    this.closeMegaMenu();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new WHeader();
});
